import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { TableProps } from "@magnetic/table";
import type { FilterFn, Row, RowData } from "@tanstack/react-table";
import type {
  ColDef,
  FilterChangedEvent,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowDragItem,
  IRowNode,
  SortComparatorFn,
  ValueGetterParams
} from "ag-grid-enterprise";

import { uuid } from "src/core/globals";
import { Constants } from "src/utils/constants";
import { deepMerge } from "src/utils/object";

import {
  agGridResourcesNumberDefault,
  agGridResourcesObjectDefault,
  agGridResourcesStringDefault,
  BaseColumnConfig
} from "../tables/columnConfigs/baseColumnConfig";

import { DetailCellRenderer } from "./typedAgGridMasterDetail";
import { TypedGrid } from "./typedGrid";
import {
  type BaseTypedTableLayoutProps,
  BaseTypedTableProps,
  Column,
  isGlobalFilterApplied,
  MagneticCompatibleRowData,
  typedGridHiddenLayout
} from "./typedTableUtil";

type AgGridProps<Column> = BaseTypedTableProps<Column> & {
  readonly agGridConfig?: Partial<GridOptions>;
} & Omit<TableProps<unknown>, "columns" | "data">;

/**
 * Type definitions borrowed from Magnetic conventions
 * `V` - custom Column Configuration class
 * `VT` - Data or value type expressed by the Column Configuration class
 * `C` - any custom context configuration for the table that will be supplied to all the AG Grid APIs
 */
export function TypedAgGridWrapper<
  V extends BaseColumnConfig<VT>,
  VT,
  C = object
>(props: AgGridProps<Column<V, VT>>) {
  const {
    agGridConfig,
    layout,
    name,
    columns,
    data = [],
    className = "",
    isLoading,
    defaultSortColumn
  } = props;

  const typedGridDivRef = useRef<HTMLDivElement>(null);

  // Temporarily hide the grid before it can calculate space to allocate in the UI
  const [computedLayoutStyle, setComputedLayoutStyle] = useState<
    BaseTypedTableLayoutProps | undefined
  >(typeof layout !== "function" ? layout : typedGridHiddenLayout);

  const globalFilterValueRef = useRef<object | string>(); // Create a ref to store the filter value
  // Preserve the global filter as a mutable reference from the client
  useEffect(() => {
    if (props.globalFilter) {
      globalFilterValueRef.current = props.globalFilter;
    }
  }, [props.globalFilter]);

  const gridApiRef = useRef<GridApi>(); // Initialize with null

  // after AG Grid filter and sort operation is performed, notify the listener
  const onAgGridFilterChanged = useCallback(
    (param: FilterChangedEvent<VT, C> | { api: GridApi }) => {
      if (typeof props.onRowListChange === "function") {
        const list: Array<IRowNode<VT>> = [];
        param.api.forEachNodeAfterFilterAndSort((rowNode) =>
          list.push(rowNode)
        );
        props.onRowListChange(list);
      }
    },
    [props]
  );

  // Invoke custom layout factory if a functional layout needs to be computed up front for the grid
  const buildGridReadyLayout = useCallback(
    ({ api }: { api: GridApi }) => {
      if (typedGridDivRef.current) {
        const resolvedLayout =
          typeof layout === "function" ? layout(api, typedGridDivRef) : layout;

        setComputedLayoutStyle(resolvedLayout);
      }
    },
    [layout]
  );

  /**
   * After grid loads initially, notify the client that row data is rendered ( not really changed )
   */
  const onAgGridReadyEvent = useCallback(
    (evt: GridReadyEvent<VT, C>) => {
      gridApiRef.current = evt.api;
      buildGridReadyLayout({ api: evt.api }); // construct layout dimensions for grid
      onAgGridFilterChanged({ api: evt.api }); // Notify the listener that data is available
    },
    [buildGridReadyLayout, onAgGridFilterChanged]
  );

  const columnDefs: Array<ColDef<VT>> = useMemo(
    () =>
      Object.entries(columns).map(
        ([field, columnConfigObject], columnSourceIndex) => {
          const config = columnConfigObject.getConfig();
          const {
            pinned,
            sortable = true,
            hidden = false,
            size,
            label,
            agGridColumnDefinition
          } = config;

          // prefer drag icon on the first column, but this could become configurable later
          const defaultRowDragColumnIndex = 0;

          const columnDef: ColDef = deepMerge(
            {
              field: field,
              pinned: pinned,
              sortable: sortable,
              hide: hidden,
              initialWidth: size,

              sort:
                defaultSortColumn && field === defaultSortColumn
                  ? "asc"
                  : undefined,

              // enable row drag only for first column when config declares row re-ordering
              rowDrag:
                (agGridColumnDefinition?.rowDrag ||
                  props.enableRowReordering) &&
                columnSourceIndex === defaultRowDragColumnIndex,

              // Client configuration must implement AG Grid `rowDragText` to customize what text to show
              // Below is fallback logic in case the developer neglected to implement it
              // The AG Grid row dragging just shows text in a box -- (not the entire row as nicely as Magnetic does)
              rowDragText:
                (agGridColumnDefinition?.rowDrag ||
                  props.enableRowReordering) &&
                columnSourceIndex === defaultRowDragColumnIndex
                  ? (params: IRowDragItem) => {
                      // Fallback to generic message which avoids showing `[Object]` or meaningless information
                      return params.rowNode
                        ? `Row item [${params.rowNode.rowIndex}]`
                        : `Unknown row node`; // This is a fallback per TypeScript. Don't think it is even possible to drag a row without a row index
                    }
                  : undefined,

              enableCellChangeFlash: props.flashOnUpdate ?? true, // permit visual transition cell background color when data updates

              // header label may be a static string or React component
              headerName: typeof label === "string" ? label : undefined,

              // If a React component is configured, assign the label as a header component
              headerComponentParams:
                typeof label !== "string" // must conform to React component
                  ? { innerHeaderComponent: () => label }
                  : undefined,

              // For performance gains, no inferred data type allowed.
              // The client Column Config class will provide a required cell type in constructor
              cellDataType: false,

              // precedence over value getter
              // Implement ColumnConfig "alternatives" support for AG Grid path as we have in Magnetic Table.
              // - Evaluate the first alternative whose `check(value)` returns true
              // - If matched, render via that alternative's `render(value)`
              // - Otherwise, fall back to the column's default renderer
              cellRenderer: (renderProps: ICellRendererParams) => {
                const cellValue = renderProps.value;
                const alternateConditional = config.alternatives?.find((x) =>
                  x.check(cellValue)
                );
                return alternateConditional
                  ? alternateConditional.render(cellValue)
                  : columnConfigObject.render(cellValue);
              },

              valueGetter: (params: ValueGetterParams<V, VT, C>) =>
                params.data?.[field as keyof V],

              // client grid may opt in custom comparator function
              // AG Grid v35 `comparator()` signature expanded to a union, which results in more discrete code branches below
              // SortComparatorFn<TData, TValue> | Partial<Record<SortType, SortComparatorFn<TData, TValue>>>
              comparator: <D extends VT | null | undefined>(
                dataA: D,
                dataB: D,
                nodeA: IRowNode<V>,
                nodeB: IRowNode<V>,
                isDescending: boolean
              ) =>
                (agGridColumnDefinition?.comparator as SortComparatorFn)?.(
                  dataA,
                  dataB,
                  nodeA,
                  nodeB,
                  isDescending
                ) ??
                columnConfigObject.compare?.(dataA!, dataB!) ??
                0,

              /**
               * Data equality checks are used extensively by AG Grid to determine if a cell's underlying data has changed
               * With that information it can add/remove rows and deal with visual updates should the row be the same,
               * but the data value in a cell might have changed in the case of real-time updates like port traffic
               * because data can be structured objects, we need ability to define custom comparators
               * either as AG Grid specific behavior.
               * Next option is to try `compare()` method on the column config class.
               * The fallback is to call `equals()` on the Column Config class (or its base class)
               */
              equals: <D extends VT | null | undefined>(dataA: D, dataB: D) => {
                // prefer AG Grid override if specified
                if (agGridColumnDefinition?.equals) {
                  return agGridColumnDefinition.equals(dataA, dataB);
                  // resort to column config comparator procedure
                } else if (columnConfigObject.compare) {
                  return (
                    columnConfigObject.compare(dataA as VT, dataB as VT) === 0
                  );
                }
                // fall back to using `isEqual()` on the base column config abstract class
                return columnConfigObject.equals(dataA as VT, dataB as VT);
              }
            } as ColDef,
            agGridColumnDefinition ?? {}
          );

          // master-detail toggle arrow rendered inside AG Grid supplied container inside first column cell
          if (props.expandable && columnSourceIndex === 0) {
            columnDef.cellRenderer = "agGroupCellRenderer";
          }

          return columnDef;
        }
      ),
    [
      columns,
      props.expandable,
      props.enableRowReordering,
      props.flashOnUpdate,
      defaultSortColumn
    ]
  );

  const defaultColDef = useMemo(
    (): ColDef<VT> => ({
      editable: false,
      lockPinned: true,
      lockVisible: true, // prevents column from becoming hidden or dragging it out of the grid
      sortable: false,
      sortingOrder: ["desc", "asc", null],
      // filter: true, -- (do not enable ) because it adds a column filter icon + dropdown
      resizable: true,
      minWidth: 100 // prevent column squeezing. For small columns with one word or icon, override in the column definition setup
    }),
    []
  );

  // Update the filter state ref whenever the state changes

  const isExternalFilterPresent = useCallback(
    () =>
      globalFilterValueRef.current
        ? isGlobalFilterApplied(globalFilterValueRef.current)
        : false,
    [] // No dependencies for this useCallback, as it relies on the ref
  );

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<VT>) => {
      if (props.globalFilterFn && gridApiRef.current) {
        // ignore filter on AG Grid's internal checkbox columns
        const globallyFilterableColumns = gridApiRef.current
          .getAllGridColumns()
          .filter(
            (col) =>
              col.isVisible() && col.getColId() !== "ag-Grid-SelectionColumn"
          );

        const resolvedGlobalFilters: Array<{
          id: ColDef["colId"];
          filterFn: FilterFn<RowData> | undefined; // to satisfy Magnetic contract
          resolvedValue: typeof globalFilterValueRef.current;
        }> = [];

        globallyFilterableColumns.forEach((column) => {
          resolvedGlobalFilters.push({
            id: column.getColId(),
            filterFn: props.globalFilterFn as FilterFn<RowData>, // to satisfy Magnetic contract
            resolvedValue: globalFilterValueRef.current
          });
        });

        let currentGlobalFilterMetadata;

        // row Index will be null IF the node is currently not displayed due it not matching the filter rules
        for (let i = 0; i < resolvedGlobalFilters.length; i++) {
          currentGlobalFilterMetadata = resolvedGlobalFilters[i]!;
          const iteratedColumnId = currentGlobalFilterMetadata.id;
          if (
            iteratedColumnId &&
            currentGlobalFilterMetadata.filterFn?.(
              node as unknown as Row<unknown>, // synthetic cast to call an original filtering function with Magnetic signatures
              iteratedColumnId,
              currentGlobalFilterMetadata.resolvedValue,
              (filterMeta) => {
                gridApiRef.current?.setColumnFilterModel(
                  iteratedColumnId,
                  filterMeta
                );
              }
            )
          ) {
            return true; // found first match. Good enough to stop searching the object
          }
        }
      } else {
        return true;
      }
      return false;
    },
    [props.globalFilterFn]
  );

  const gridOptions = useMemo((): GridOptions<VT> => {
    /**
     * The column definition configuration should add "rowIdentifier" attribute ot least one, but possibly more columns to uniquely identify the row.
     * AG Grid requires a fast and deterministic way to locate a row, especially as server-dispatched updates change row data.
     *
     * Check that "rowIdentifier" property is true or set as callback for generating key
     *
     * For server side updates to reflect changes to existing data, AG Grid requires a consistent way to uniquely find the row
     * Example: if user annotated the macAddress field and pid columns, then a possible row ID is:
     */

    function GetRowIdentifierFactory<TDATA, C = object>(
      columnDefinitionMeta: Record<string, BaseColumnConfig<TDATA>>
    ): (params: GetRowIdParams<NonNullable<TDATA>, C>) => string {
      const generateUnknownRowIdentifier = () => `row-${uuid()}`;

      const keySeparator = ":";
      const acceptableCellDataTypeList = [
        agGridResourcesStringDefault.cellDataType,
        agGridResourcesNumberDefault.cellDataType,
        agGridResourcesObjectDefault.cellDataType
      ];

      const columnKeyCandidateList = Object.entries(columnDefinitionMeta)
        .filter(([, value]) => !!value.getConfig().rowIdentifier)
        .map(([key]) => key);

      // if list of candidate keys was empty, build a new list if the `cellDataType` is supported for making an identifier
      if (!columnKeyCandidateList.length) {
        Object.entries(columnDefinitionMeta)
          .filter(([, value]) => {
            const configColDef = value.getConfig().agGridColumnDefinition;
            return configColDef?.cellDataType
              ? acceptableCellDataTypeList.includes(
                  configColDef.cellDataType
                ) && value.getConfig().rowIdentifier !== false
              : false;
          })
          .forEach(([key, value]) => {
            // Regarding direction mutation of a property.
            // Note: This will never override any existing custom function ( because of the conditional check above )
            // it is activating an otherwise undefined rowIdentifier to act as if it were true
            // When none of the columns were explicitly stated to act as row identifiers, it means the UI
            // should essentially turn all of them on to participate in the row identifier generation
            value.getConfig().rowIdentifier = true;
            columnKeyCandidateList.push(key);
          });
      }

      return (params: GetRowIdParams<NonNullable<TDATA>, C>) => {
        const generatedRowIdentifier = params.data
          ? Object.entries<TDATA>(params.data)
              .filter(
                ([property, value]) =>
                  columnKeyCandidateList.includes(property) &&
                  value !== undefined &&
                  value !== null &&
                  value !== Constants.EMPTY_VALUE
              )
              .map(([property, value]) => {
                const config = columnDefinitionMeta[property]!.getConfig();

                // interface: rowIdentifier?: ((data: T) => string | undefined) | boolean;
                return typeof config.rowIdentifier === "function"
                  ? config.rowIdentifier(value)
                  : columnDefinitionMeta[property]?.identity(value) ?? value;
              })
              .filter((val) => val !== "" && val !== undefined)
              .join(keySeparator)
          : undefined;

        return generatedRowIdentifier || generateUnknownRowIdentifier();
      };
    }

    const options: GridOptions<VT> = deepMerge(
      {
        gridId: name,

        //  provides unique identifier for each row, allowing for efficient change detection
        getRowId: GetRowIdentifierFactory(props.columns),

        /**
         Developer Note: the client table should specify its desired strategy.
         AG grid default is still usable: Columns/Cell data will fit nicely in the allocated width of the parent layout container.
         Another good option is type: "fitGridWidth" --  Sizes columns to fit grid width, with the goal of not having a horizontal scrollbar

         autoSizeStrategy: {
         type: "fitCellContents", // there may be a horizontal scrollbar
         defaultMinWidth: 100
         },
         */

        suppressAutoSize: false,
        // UX: keep perfect column alignment as the browser resizes. Note: rapid layout calculations should be throttled
        onGridSizeChanged: (params) => params.api.sizeColumnsToFit(),

        defaultColDef,

        // notifier when Grid API ready
        onGridReady: (event: GridReadyEvent<VT>) => {
          onAgGridReadyEvent(event);
          agGridConfig?.onGridReady?.(event); // client code registration
        }
      } as GridOptions<VT>,
      agGridConfig ?? {}
    );

    // grid options support for dragging a row
    if (props.enableRowReordering) {
      options.rowDragManaged ??= true;
      // Show a blue horizontal rule line between each row as the intended drop zone visual indicator
      options.suppressMoveWhenRowDragging ??= true;
    }

    // augment grid with row selection interaction
    if (props.selectableRows) {
      // assumes multiselection
      options.rowSelection ??= {
        mode: "multiRow",
        headerCheckbox: true,

        // define custom rule for determining if a row is selectable or disabled ( Software Management table use case )
        isRowSelectable: (rowNode: IRowNode<VT>) =>
          typeof props.selectableRows === "function"
            ? props.selectableRows({
                original: rowNode.data as MagneticCompatibleRowData<VT>
              })
            : true
      };

      // when user toggles a row selection in the grid UI, notify the client handler
      options.onSelectionChanged ??= (selectionChangedEvent) => {
        const selection = selectionChangedEvent.selectedNodes;

        if (
          selection &&
          selectionChangedEvent.type === "selectionChanged" &&
          props.onRowSelectionChange
        ) {
          const selectedRowIndexSet = new Set(
            selection.map((_) => _.sourceRowIndex)
          );
          const selectedRowHashMap = Object.fromEntries(
            new Map([...selectedRowIndexSet].map((value) => [value, true]))
          );

          props.onRowSelectionChange(selectedRowHashMap);
        }
      };

      options.selectionColumnDef ??= {
        suppressHeaderContextMenu: true, // UX: disable right click menu
        resizable: false,
        suppressHeaderMenuButton: true, // UX opinion, perhaps turn off the menu options ("3 dots") for sorting checkbox column
        pinned: "left"
      };
    }

    // filtering
    if (typeof props.globalFilterFn === "function") {
      options.isExternalFilterPresent = isExternalFilterPresent;
      options.doesExternalFilterPass = doesExternalFilterPass;

      // update the results count in the external filter
      if (props.onRowListChange) {
        options.onFilterChanged = onAgGridFilterChanged;
      }
    }

    // master-detail setup from column definition config
    if (props.expandable) {
      options.masterDetail = true;
      options.detailCellRenderer = DetailCellRenderer;
      options.detailRowAutoHeight = true; // UX: let the detail component specify its height to allocate how tall expanded row will be
    }

    return options;
  }, [
    props,
    name,
    defaultColDef,
    agGridConfig,
    onAgGridReadyEvent,
    isExternalFilterPresent,
    doesExternalFilterPass,
    onAgGridFilterChanged
  ]);

  // alias to rowData directly
  gridOptions.rowData = data as VT[];

  // update from customized client-defined global filter
  useEffect(() => {
    if (!(gridApiRef.current && props.globalFilter)) {
      return;
    }
    gridApiRef.current.onFilterChanged("api"); // manually deal with change detection of external filter component
  }, [props.globalFilter]);

  // row selection
  // Custom components external to the grid instance can specify a map of selected row indexes.
  // Note: the RowActionsBar plugin component will set different or (reset to empty ) selection rows
  useEffect(() => {
    if (
      !(
        gridApiRef.current &&
        gridOptions &&
        props.selectableRows &&
        props.rowSelection
      )
    ) {
      return;
    }

    // quick clearing of all selections
    if (!Object.keys(props.rowSelection).length) {
      if (gridApiRef.current.getSelectedNodes().length > 0) {
        gridApiRef.current.deselectAll();
      }
      return;
    }

    const entries = Object.entries(props.rowSelection);

    // extract tuples from map having a value of "true" and transform back to an array of row indexed integers
    const selectedRowIndexList = Object.keys(
      Object.fromEntries(entries.filter(([, value]) => value))
    ).map((key) => +key);

    // scan for matches, and if found remove the index in the selected row index list
    // set the selected state for the checkbox
    // calculate rows inclusive of filtering operations
    // logic may be imperfect in the case of filtered rows

    // Note: sourceRowIndex is essential for tracking rows not visible due to filtering
    gridApiRef.current.forEachNode((node) => {
      if (node.sourceRowIndex !== null) {
        const foundIndex = selectedRowIndexList.indexOf(node.sourceRowIndex);

        node.setSelected(foundIndex >= 0);
        if (foundIndex >= 0) {
          selectedRowIndexList.splice(foundIndex, 1);
        }
      }
    });
  }, [gridOptions, props.selectableRows, props.rowSelection]);

  // /------------- Resolve table implementation and render ---------------------------

  return (
    <TypedGrid
      ref={typedGridDivRef}
      rowData={data}
      columnDefs={columnDefs}
      layout={computedLayoutStyle}
      gridOptions={gridOptions}
      className={className}
      loading={isLoading}
    >
      {props.children}
    </TypedGrid>
  );
}
